import React, { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaBan, FaStop, FaEye, FaMousePointer, FaPhoneAlt, FaVideo, FaBell } from 'react-icons/fa';
import { AD_FORMS, AD_STATUS, PLACEMENT_LABELS, StatusChip, formatDate, formatMoney } from './shopConstants';

const FILTERS = [
    { value: 'pending', label: 'بانتظار الموافقة' },
    { value: 'scheduled', label: 'مجدولة' },
    { value: 'active', label: 'نشطة' },
    { value: 'expired', label: 'منتهية' },
    { value: 'rejected', label: 'مرفوضة' },
    { value: 'cancelled', label: 'ملغاة' },
    { value: '', label: 'الكل' },
];

const daysLeft = (endsAt) => Math.max(0, Math.ceil((new Date(endsAt) - Date.now()) / 86400000));

// <input type="datetime-local"> value for a Date (local time).
const toLocalInput = (date) => {
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const NOTIFICATION_STATUS = {
    sent: 'تم إرسال الإشعار',
    scheduled: 'إشعار مجدول',
    cancelled: 'أُلغي الإشعار المجدول',
};

// Users' notification line on an ad card.
const NotificationStatus = ({ notification }) => {
    if (!notification || !NOTIFICATION_STATUS[notification.status]) return null;
    const { status } = notification;
    return (
        <div className={`ad-notification ${status}`}>
            <FaBell /> {NOTIFICATION_STATUS[status]}
            {status === 'sent' && notification.sentAt ? <> · {formatDate(notification.sentAt, true)}{notification.sentCount ? ` · ${notification.sentCount} مستخدم` : ''}</> : null}
            {status === 'scheduled' && notification.sendAt ? <> · {formatDate(notification.sendAt, true)}</> : null}
            <div className="muted small">"{notification.title}" — {notification.body}</div>
        </div>
    );
};

// Approval dialog: optionally notify all users - the owner's draft, editable - now or at a set time.
const ApproveBox = ({ ad, busy, onApprove, onCancel }) => {
    const draft = ad.userNotification || {};
    const [enabled, setEnabled] = useState(Boolean(draft.title && draft.body));
    const [title, setTitle] = useState(draft.title || `عرض جديد من ${ad.shop?.name || 'ورشة'}`);
    const [body, setBody] = useState(draft.body || '');
    const [when, setWhen] = useState('now');
    const [sendAt, setSendAt] = useState(toLocalInput(Date.now() + 60 * 60 * 1000));
    const scheduledInPast = when === 'scheduled' && new Date(sendAt).getTime() <= Date.now() + 60 * 1000;
    const invalid = enabled && (!title.trim() || !body.trim() || scheduledInPast);
    const startsLater = ad.startsAt && new Date(ad.startsAt).getTime() > Date.now();
    const lateDays = ad.startsAt && !startsLater ? Math.floor((Date.now() - new Date(ad.startsAt).getTime()) / 86400000) : 0;

    return (
        <div className="reason-box compact approve-box">
            {startsLater && <div className="muted small">موعد الحجز يبدأ {formatDate(ad.startsAt)}: سيصبح الإعلان مجدولاً ويظهر تلقائياً في موعده، والإشعار يُرسل عند البداية على الأقل.</div>}
            {lateDays >= 1 && <div className="reason-note">بدأ موعد الحجز منذ {lateDays} يوم: ستُعاد قيمة الأيام الفائتة لصاحب الورشة تلقائياً.</div>}
            <label className="inline-check">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                إرسال إشعار لجميع المستخدمين {draft.title ? <span className="muted small">(كتبه صاحب الورشة، يمكنك تعديله)</span> : null}
            </label>
            {enabled && (
                <>
                    <input type="text" maxLength={60} placeholder="عنوان الإشعار" value={title} onChange={(e) => setTitle(e.target.value)} />
                    <textarea rows={2} maxLength={180} placeholder="نص الإشعار" value={body} onChange={(e) => setBody(e.target.value)} />
                    <div className="send-when">
                        <label className="inline-check">
                            <input type="radio" name={`when-${ad._id}`} checked={when === 'now'} onChange={() => setWhen('now')} /> الآن
                        </label>
                        <label className="inline-check">
                            <input type="radio" name={`when-${ad._id}`} checked={when === 'scheduled'} onChange={() => setWhen('scheduled')} /> في وقت محدد
                        </label>
                        {when === 'scheduled' && (
                            <input type="datetime-local" value={sendAt} min={toLocalInput(Date.now())} onChange={(e) => setSendAt(e.target.value)} />
                        )}
                    </div>
                    {scheduledInPast && <div className="reason-note">اختر وقتاً في المستقبل</div>}
                </>
            )}
            <div className="modal-actions">
                <button
                    type="button"
                    className="btn success"
                    disabled={busy || invalid}
                    onClick={() => onApprove({
                        notification: enabled
                            ? { enabled: true, title: title.trim(), body: body.trim(), sendAt: when === 'scheduled' ? new Date(sendAt).toISOString() : null }
                            : { enabled: false },
                    })}
                >
                    <FaCheck /> تأكيد التفعيل
                </button>
                <button type="button" className="btn ghost" onClick={onCancel}>إلغاء</button>
            </div>
        </div>
    );
};

const AdCard = ({ ad, actions, onChanged }) => {
    const [mode, setMode] = useState(null); // 'approve' | 'reject' | 'takedown'
    const [reason, setReason] = useState('');
    const [refund, setRefund] = useState(true);

    const confirm = async () => {
        const ok = mode === 'reject'
            ? await actions.rejectAd(ad._id, reason.trim())
            : await actions.takeDownAd(ad._id, reason.trim(), refund);
        if (ok) onChanged();
    };

    return (
        <article className="ad-card">
            {/* One creative per placement (older ads have a single media). */}
            {(ad.creatives?.length ? ad.creatives : [{ placement: null, type: ad.media?.type, url: ad.media?.url, caption: ad.caption }]).map((c, i) => (
                <div key={`${c.placement}-${i}`} className="ad-creative">
                    {/* Header ads: square media since the banner redesign (older ones are 3:1). */}
                    <div className="ad-media" style={c.placement === 'home_header' ? { aspectRatio: c.title ? '1 / 1' : '3 / 1', maxHeight: 260 } : undefined}>
                        {c.type === 'video' ? (
                            <>
                                <video src={c.url} controls preload="metadata" />
                                <span className="media-tag"><FaVideo /> فيديو</span>
                            </>
                        ) : (
                            <a href={c.url} target="_blank" rel="noreferrer"><img src={c.url} alt="" /></a>
                        )}
                        {c.placement && <span className="placement-tag">{PLACEMENT_LABELS[c.placement] || c.placement}</span>}
                    </div>
                    {c.title ? (
                        <div className="creative-caption">
                            <strong>{c.title}</strong>{c.subtitle ? <> · <span className="accent">{c.subtitle}</span></> : null}
                        </div>
                    ) : null}
                    {c.caption && ad.creatives?.length ? <div className="creative-caption">"{c.caption}"</div> : null}
                </div>
            ))}
            <div className="ad-body">
                <div className="ad-head">
                    {ad.shop?.logo && <img className="ad-shop-logo" src={ad.shop.logo} alt="" />}
                    <div>
                        <strong>{ad.shop?.name || 'ورشة محذوفة'}</strong>
                        <div className="muted small">{ad.owner?.ownerName} · <bdi>{ad.owner?.phoneNumber}</bdi></div>
                    </div>
                    <StatusChip status={ad.status} map={AD_STATUS} />
                </div>
                {ad.caption && !ad.creatives?.length && <p className="ad-caption">"{ad.caption}"</p>}
                <div className="ad-package">
                    <span className="package-name">{ad.packageSnapshot?.name}</span>
                    <span>{ad.packageSnapshot?.durationDays} يوم</span>
                    <span>{formatMoney(ad.pricePaid)}</span>
                </div>
                <div className="chips">
                    {(ad.packageSnapshot?.placements || []).map((p) => <span key={p} className="chip small">{PLACEMENT_LABELS[p] || p}</span>)}
                </div>
                <div className="ad-dates muted small">
                    طُلب: {formatDate(ad.createdAt, true)}
                    {ad.startsAt && <> · الحجز من <strong>{formatDate(ad.startsAt)}</strong> إلى <strong>{formatDate(ad.endsAt)}</strong></>}
                    {ad.status === 'active' && <strong className="days-left"> · متبقي {daysLeft(ad.endsAt)} يوم</strong>}
                </div>
                {(ad.rejectionReason && ad.status !== 'active') && <div className="reason-note">السبب: {ad.rejectionReason}</div>}
                {ad.refundedAmount > 0 && <div className="muted small">تم إرجاع {formatMoney(ad.refundedAmount)}</div>}
                <NotificationStatus notification={ad.userNotification} />
                {ad.status === 'pending' && ad.userNotification?.title ? (
                    <div className="ad-notification draft"><FaBell /> مسودة إشعار من صاحب الورشة: "{ad.userNotification.title}" — {ad.userNotification.body}</div>
                ) : null}
                {ad.status !== 'pending' && (
                    <div className="ad-stats">
                        <span><FaEye /> {ad.stats?.impressions || 0}</span>
                        <span><FaMousePointer /> {ad.stats?.clicks || 0}</span>
                        <span><FaPhoneAlt /> {ad.stats?.calls || 0}</span>
                    </div>
                )}

                {mode === 'approve' ? (
                    <ApproveBox
                        ad={ad}
                        busy={actions.busy}
                        onCancel={() => setMode(null)}
                        onApprove={async (body) => {
                            await actions.approveAd(ad._id, body);
                            // Refresh either way: the ad may be live even if its notification failed.
                            onChanged();
                        }}
                    />
                ) : mode ? (
                    <div className="reason-box compact">
                        <textarea rows={2} placeholder={mode === 'reject' ? 'سبب الرفض (يصل لصاحب الورشة)' : 'سبب الإيقاف'} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
                        {mode === 'takedown' && (
                            <label className="inline-check">
                                <input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} />
                                إرجاع قيمة الأيام المتبقية لرصيد صاحب الورشة
                            </label>
                        )}
                        <div className="modal-actions">
                            <button type="button" className="btn danger" disabled={!reason.trim() || actions.busy} onClick={confirm}>تأكيد</button>
                            <button type="button" className="btn ghost" onClick={() => { setMode(null); setReason(''); }}>إلغاء</button>
                        </div>
                    </div>
                ) : (
                    <div className="modal-actions">
                        {ad.status === 'pending' && (
                            <>
                                <button type="button" className="btn success" disabled={actions.busy} onClick={() => setMode('approve')}>
                                    <FaCheck /> تفعيل الإعلان
                                </button>
                                <button type="button" className="btn danger-outline" onClick={() => setMode('reject')}><FaBan /> رفض وإرجاع المبلغ</button>
                            </>
                        )}
                        {(ad.status === 'active' || ad.status === 'scheduled') && (
                            <button type="button" className="btn warning-outline" onClick={() => setMode('takedown')}><FaStop /> إيقاف الإعلان</button>
                        )}
                    </div>
                )}
            </div>
        </article>
    );
};

const AdsPanel = ({ actions, summary }) => {
    const [status, setStatus] = useState('pending');
    const [placement, setPlacement] = useState('');
    const [data, setData] = useState({ ads: [], total: 0, page: 1, pageSize: 20 });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const json = await actions.listAds({ status, page, ...(placement ? { placement } : {}) });
        if (json) setData(json);
        setLoading(false);
    }, [actions.listAds, status, page, placement]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const pages = Math.max(1, Math.ceil(data.total / data.pageSize));

    return (
        <div className="panel">
            <div className="panel-toolbar">
                <div className="segmented">
                    {FILTERS.map((f) => (
                        <button type="button" key={f.value} className={status === f.value ? 'active' : ''} onClick={() => { setStatus(f.value); setPage(1); }}>
                            {f.label}
                            {f.value === 'pending' && summary?.pendingAds > 0 && <span className="badge">{summary.pendingAds}</span>}
                        </button>
                    ))}
                </div>
                <select className="form-filter" value={placement} onChange={(e) => { setPlacement(e.target.value); setPage(1); }}>
                    <option value="">كل الأنواع</option>
                    {AD_FORMS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
            </div>
            {loading ? (
                <div className="loading">جاري التحميل...</div>
            ) : data.ads.length === 0 ? (
                <div className="empty-state">لا توجد إعلانات في هذا القسم</div>
            ) : (
                <div className="ads-grid">
                    {data.ads.map((ad) => <AdCard key={ad._id} ad={ad} actions={actions} onChanged={load} />)}
                </div>
            )}
            {pages > 1 && (
                <div className="pagination">
                    <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>السابق</button>
                    <span>{page} / {pages}</span>
                    <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>التالي</button>
                </div>
            )}
        </div>
    );
};

export default AdsPanel;
